import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, Observable } from 'rxjs';
import { MessagePayload } from 'firebase/messaging';

export interface NotificationData {
  id: string;
  title: string;
  body: string;
  timestamp: Date;
  read: boolean;
  data?: any;
  icon?: string;
}

@Injectable({
  providedIn: 'root'
})
export class NotificationService {
  private notifications: NotificationData[] = [];
  private notificationsSubject = new BehaviorSubject<NotificationData[]>([]);
  private unreadCountSubject = new BehaviorSubject<number>(0);

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      this.loadNotificationsFromStorage();
    }
  }

  /**
   * Add a new notification
   */
  addNotification(payload: MessagePayload): void {
    const notification: NotificationData = {
      id: this.generateId(),
      title: payload.notification?.title || 'New Notification',
      body: payload.notification?.body || '',
      timestamp: new Date(),
      read: false,
      data: payload.data,
      icon: payload.notification?.image
    };

    this.notifications.unshift(notification);
    this.updateSubjects();
    this.saveNotificationsToStorage();
  }

  /**
   * Get all notifications as observable
   */
  getNotifications(): Observable<NotificationData[]> {
    return this.notificationsSubject.asObservable();
  }

  /**
   * Get unread count as observable
   */
  getUnreadCount(): Observable<number> {
    return this.unreadCountSubject.asObservable();
  }

  /**
   * Mark notification as read
   */
  markAsRead(id: string): void {
    const notification = this.notifications.find(n => n.id === id);
    if (notification && !notification.read) {
      notification.read = true;
      this.updateSubjects();
      this.saveNotificationsToStorage();
    }
  }

  /**
   * Mark all notifications as read
   */
  markAllAsRead(): void {
    let hasChanges = false;
    this.notifications.forEach(notification => {
      if (!notification.read) {
        notification.read = true;
        hasChanges = true;
      }
    });

    if (hasChanges) {
      this.updateSubjects();
      this.saveNotificationsToStorage();
    }
  }

  /**
   * Delete a notification
   */
  deleteNotification(id: string): void {
    const index = this.notifications.findIndex(n => n.id === id);
    if (index > -1) {
      this.notifications.splice(index, 1);
      this.updateSubjects();
      this.saveNotificationsToStorage();
    }
  }

  /**
   * Delete all notifications
   */
  deleteAllNotifications(): void {
    this.notifications = [];
    this.updateSubjects();
    this.saveNotificationsToStorage();
  }

  /**
   * Delete all read notifications
   */
  deleteReadNotifications(): void {
    this.notifications = this.notifications.filter(n => !n.read);
    this.updateSubjects();
    this.saveNotificationsToStorage();
  }

  /**
   * Get notifications by status
   */
  getNotificationsByStatus(read: boolean): NotificationData[] {
    return this.notifications.filter(n => n.read === read);
  }

  /**
   * Search notifications
   */
  searchNotifications(query: string): NotificationData[] {
    const lowercaseQuery = query.toLowerCase();
    return this.notifications.filter(n =>
      n.title.toLowerCase().includes(lowercaseQuery) ||
      n.body.toLowerCase().includes(lowercaseQuery)
    );
  }

  /**
   * Get notifications with pagination
   */
  getNotificationsPaginated(page: number, limit: number): NotificationData[] {
    const startIndex = (page - 1) * limit;
    const endIndex = startIndex + limit;
    return this.notifications.slice(startIndex, endIndex);
  }

  /**
   * Get total pages for pagination
   */
  getTotalPages(limit: number): number {
    return Math.ceil(this.notifications.length / limit);
  }

  /**
   * Update subjects with current state
   */
  private updateSubjects(): void {
    this.notificationsSubject.next([...this.notifications]);
    this.unreadCountSubject.next(this.notifications.filter(n => !n.read).length);
  }

  /**
   * Generate unique ID for notification
   */
  private generateId(): string {
    return Date.now().toString() + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Save notifications to localStorage
   */
  private saveNotificationsToStorage(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      localStorage.setItem('app_notifications', JSON.stringify(this.notifications));
    } catch (error) {
      console.error('Error saving notifications to storage:', error);
    }
  }

  /**
   * Load notifications from localStorage
   */
  private loadNotificationsFromStorage(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    try {
      const stored = localStorage.getItem('app_notifications');
      if (stored) {
        this.notifications = JSON.parse(stored).map((n: any) => ({
          ...n,
          timestamp: new Date(n.timestamp)
        }));
        this.updateSubjects();
      }
    } catch (error) {
      console.error('Error loading notifications from storage:', error);
      this.notifications = [];
    }
  }

  /**
   * Clear old notifications (older than specified days)
   */
  clearOldNotifications(days: number = 30): void {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    const initialLength = this.notifications.length;
    this.notifications = this.notifications.filter(n => n.timestamp > cutoffDate);

    if (this.notifications.length < initialLength) {
      this.updateSubjects();
      this.saveNotificationsToStorage();
    }
  }

  /**
   * Get notification statistics
   */
  getStatistics(): {
    total: number;
    read: number;
    unread: number;
    today: number;
    thisWeek: number;
  } {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisWeek = new Date(today.getTime() - (7 * 24 * 60 * 60 * 1000));

    return {
      total: this.notifications.length,
      read: this.notifications.filter(n => n.read).length,
      unread: this.notifications.filter(n => !n.read).length,
      today: this.notifications.filter(n => n.timestamp >= today).length,
      thisWeek: this.notifications.filter(n => n.timestamp >= thisWeek).length
    };
  }

  /**
   * Export notifications as JSON
   */
  exportNotifications(): string {
    return JSON.stringify(this.notifications, null, 2);
  }

  /**
   * Import notifications from JSON
   */
  importNotifications(jsonData: string): boolean {
    try {
      const importedNotifications = JSON.parse(jsonData);
      if (Array.isArray(importedNotifications)) {
        this.notifications = importedNotifications.map((n: any) => ({
          ...n,
          timestamp: new Date(n.timestamp)
        }));
        this.updateSubjects();
        this.saveNotificationsToStorage();
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error importing notifications:', error);
      return false;
    }
  }

  /**
   * Set notification preferences
   */
  setNotificationPreferences(preferences: {
    showBrowserNotifications: boolean;
    autoMarkAsRead: boolean;
    maxNotifications: number;
  }): void {
    if (!isPlatformBrowser(this.platformId)) return;

    localStorage.setItem('notification_preferences', JSON.stringify(preferences));
  }

  /**
   * Get notification preferences
   */
  getNotificationPreferences(): {
    showBrowserNotifications: boolean;
    autoMarkAsRead: boolean;
    maxNotifications: number;
  } {
    if (!isPlatformBrowser(this.platformId)) {
      return {
        showBrowserNotifications: true,
        autoMarkAsRead: false,
        maxNotifications: 100
      };
    }

    try {
      const stored = localStorage.getItem('notification_preferences');
      return stored ? JSON.parse(stored) : {
        showBrowserNotifications: true,
        autoMarkAsRead: false,
        maxNotifications: 100
      };
    } catch {
      return {
        showBrowserNotifications: true,
        autoMarkAsRead: false,
        maxNotifications: 100
      };
    }
  }
}